/**
 * ApplyGroupSsids -- "Apply this group's SSIDs now" (OP#1778 point 3, 2026-10-12).
 *
 * A tag (group) change is a DB-only write; when it changes which GROUP-targeted SSIDs belong
 * on an AP, this panel says so and offers ONE config push to that AP (backend
 * POST /devices/{serial}/ssids/apply-targets). Used on the device sheet and after a bulk tag
 * in the Devices list. SSIDs on "All APs in site" are never touched by it.
 */
import { useState } from "react";
import { useApplyTargetSsids } from "@/lib/api/hooks";
import { ApiError } from "@/lib/api/client";
import { Button } from "@/components/ui/Button";

/**
 * "Apply this group's SSIDs now" (OP#1778 point 3). Shown after a tag change that moves
 * group-targeted SSIDs onto or off this AP. One config push to THIS AP only; the backend
 * refuses (409) a push that would leave the AP with no SSID until the operator ticks the box.
 * SSIDs on "All APs in site" are never touched by this.
 */
export function ApplyGroupSsids({
  serial,
  changes,
  onDone,
}: {
  serial: string;
  changes: { add: string[]; remove: string[] };
  onDone: () => void;
}) {
  const apply = useApplyTargetSsids(serial);
  const [needsAck, setNeedsAck] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const run = () => {
    if (apply.isPending) return; // double-click guard: one push per click
    setError(null);
    apply.mutate(ack ? { acknowledge: true } : {}, {
      onSuccess: (res) => {
        const s = res.summary;
        setResult(
          s
            ? `Applied: ${s.applied ?? 0} of ${s.total ?? 1} AP. The AP drops Wi-Fi for about 4 minutes while it applies.`
            : "Sent.",
        );
      },
      onError: (err) => {
        const body = (err instanceof ApiError ? err.body : null) as {
          error?: string;
          needs_confirmation?: boolean;
        } | null;
        if (err instanceof ApiError && err.status === 409 && body?.needs_confirmation) {
          setNeedsAck(body.error ?? "This AP would broadcast no Wi-Fi at all.");
        } else {
          setError(body?.error ?? (err as Error).message);
        }
      },
    });
  };

  return (
    <div role="status" className="mt-2 rounded border border-warn/40 bg-warn-soft px-3 py-2 text-xs text-ink">
      <p className="font-semibold text-warn">This group change affects this AP&rsquo;s SSIDs</p>
      <ul className="mt-1 list-disc pl-4">
        {changes.add.length > 0 && <li>Should now broadcast: {changes.add.join(", ")}</li>}
        {changes.remove.length > 0 && <li>Should stop broadcasting: {changes.remove.join(", ")}</li>}
      </ul>
      <p className="mt-1 text-ink-muted">
        Nothing has been pushed yet. Applying sends one configuration push to this AP (about 4 minutes of Wi-Fi
        disruption on it). SSIDs on &ldquo;All APs in site&rdquo; are not changed.
      </p>
      {needsAck && (
        <label className="mt-2 flex items-start gap-2 font-medium text-critical">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          <span>{needsAck} I understand.</span>
        </label>
      )}
      {error && <p className="mt-1 text-critical">{error}</p>}
      {result && <p className="mt-1 text-healthy">{result}</p>}
      <div className="mt-2 flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onDone}>
          {result ? "Close" : "Not now"}
        </Button>
        {!result && (
          <Button
            variant="primary"
            type="button"
            disabled={apply.isPending || (!!needsAck && !ack)}
            onClick={run}
          >
            {apply.isPending ? "Applying…" : "Apply this group’s SSIDs now"}
          </Button>
        )}
      </div>
    </div>
  );
}
